/**
 * Shared schedule/cron helpers for integrations (AWS, GCP, etc.)
 */

export type Frequency = "1hour" | "2hours" | "6hours" | "12hours" | "24hours";

export const DEFAULT_FREQUENCY: Frequency = "1hour";

export const FREQUENCY_OPTIONS: Array<{ value: Frequency; label: string }> = [
  { value: "1hour", label: "1 hour" },
  { value: "2hours", label: "2 hours" },
  { value: "6hours", label: "6 hours" },
  { value: "12hours", label: "12 hours" },
  { value: "24hours", label: "24 hours (daily)" },
];

//Cron Expressions are in the format: minute hour dayOfMonth month dayOfWeek
export function getCronExpression(
  frequency: string,
  time: Date | null,
): string {
  switch (frequency) {
    case "1hour":
      return "0 */1 * * *";
    case "2hours":
      return "0 */2 * * *";
    case "6hours":
      return "0 */6 * * *";
    case "12hours":
      return "0 */12 * * *";
    case "24hours":
    case "Daily":
    default: {
      const hour = time?.getHours() || 0;
      const minute = time?.getMinutes() || 0;
      return `${minute} ${hour} * * *`;
    }
  }
}

export interface ParsedSchedule {
  frequency: Frequency;
  time: Date | null;
}

export function parseCronToSchedule(cronExpression: string): ParsedSchedule {
  const parts = cronExpression.trim().split(/\s+/);
  if (parts.length < 5) {
    return { frequency: DEFAULT_FREQUENCY, time: null };
  }

  const [minuteStr, hourStr] = parts;

  // Check for interval patterns: */N in hour field
  if (hourStr.startsWith("*/")) {
    const interval = parseInt(hourStr.slice(2), 10);
    const intervalMap: Record<number, Frequency> = {
      1: "1hour",
      2: "2hours",
      6: "6hours",
      12: "12hours",
    };
    if (intervalMap[interval]) {
      return { frequency: intervalMap[interval], time: null };
    }
  }

  // Daily pattern: specific minute and hour
  if (minuteStr !== "*" && hourStr !== "*") {
    const minute = parseInt(minuteStr, 10);
    const hour = parseInt(hourStr, 10);
    if (
      !isNaN(hour) &&
      !isNaN(minute) &&
      hour >= 0 &&
      hour < 24 &&
      minute >= 0 &&
      minute < 60
    ) {
      const time = new Date();
      time.setHours(hour, minute, 0, 0);
      return { frequency: "24hours", time };
    }
  }

  return { frequency: DEFAULT_FREQUENCY, time: null };
}
